/**
 * ActionDetailFilters Component
 *
 * Displays filter chips for action data with dynamic cross-filter counts.
 *
 * Common filters (actionType, actionSimple, actionSize, direct, primaryOutcome,
 * players, defenders) are ALWAYS visible regardless of unique value count.
 *
 * Type-specific filters (pickType, bhrDefense, etc.) appear when all actions
 * share a single actionType. These auto-hide when ≤1 unique value.
 *
 * Parallels ShotDetailFilters but with action-specific filter categories.
 * Reuses the same chip/dropdown patterns and CSS classes.
 */

import { useMemo } from "react";
import {
  Box,
  Typography,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  FormControl,
  Select,
  OutlinedInput,
  MenuItem,
  Checkbox,
  ListItemText,
  IconButton,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import OpenInNewOutlined from "@mui/icons-material/OpenInNewOutlined";
import {
  Action,
  ActionFilters,
  ActionType,
  ActionSize,
  ActionDetailFiltersProps,
  LineupKey,
  LineupMode,
  TYPE_SPECIFIC_FIELDS,
  TypeSpecificFieldConfig,
  isLineupKey,
  isShotFilterKey,
} from "./types";
import {
  applyActionFilters,
  applyActionFiltersExcept,
  stripLineupFilters,
  toggleActionFilter,
  formatActionType,
  formatActionSize,
  formatPct,
  getHeatColor,
} from "./utils";
import { usePlayerTable } from "./PlayerTableContext";
import { ACTION_KEY_TO_FILTER } from "../utils/buildExplorerQuery";
import "../ShotDetailModal/ShotDetailModal.scss";
import { pickTypeLabel } from "../pickTypes";

// ============================================================================
// Filter Option Discovery
// ============================================================================

interface FilterOption<T> {
  value: T;
  label: string;
  count: number;
  aPPP: number | null;
}

interface PlayerOption {
  /** Interned player index used as the filter value. */
  index: number;
  /** Display name resolved from the player intern table. */
  name: string;
  count: number;
  aPPP: number | null;
}

/**
 * Single-pass helper that groups cross-filtered actions by option value
 * and computes both count and aPPP (via chanceId deduplication) per value.
 */
function computeOptionStats<T>(
  crossFiltered: Action[],
  getKey: (a: Action) => T | null,
): Map<T, { count: number; aPPP: number | null }> {
  const data = new Map<T, { count: number; chances: Map<string, number> }>();
  for (const a of crossFiltered) {
    const key = getKey(a);
    if (key === null) continue;
    if (!data.has(key)) data.set(key, { count: 0, chances: new Map() });
    const d = data.get(key)!;
    d.count++;
    // aPPP over valid chances only, as the summary rows and the report
    if (a.chanceId && a.valid !== false && !d.chances.has(a.chanceId)) {
      d.chances.set(a.chanceId, a.aPts ?? 0);
    }
  }
  const result = new Map<T, { count: number; aPPP: number | null }>();
  for (const [key, d] of data) {
    const chanceCount = d.chances.size;
    const totalAPts = [...d.chances.values()].reduce((sum, v) => sum + v, 0);
    result.set(key, {
      count: d.count,
      aPPP: chanceCount > 0 ? totalAPts / chanceCount : null,
    });
  }
  return result;
}

/**
 * Discover common filter options and compute cross-filter counts.
 * Common filters are ALWAYS shown (visibility is always true).
 */
function useCommonFilterOptions(
  actions: Action[],
  filters: ActionFilters,
  players: string[],
  lineups: ActionDetailFiltersProps["lineups"],
) {
  return useMemo(() => {
    const resolveName = (idx: number) => players[idx] ?? `#${idx}`;
    // Exclude no_action from all action filter chip pools — they're controlled by Chance Filters only
    const realActions = actions.filter((a) => a.actionType !== "no_action");

    // Count unique values in full (unfiltered) data — hide groups with ≤1 unique value
    const uniqueActionTypes = new Set(realActions.map((a) => a.actionType))
      .size;
    const uniqueActionSimple = new Set(
      realActions.map((a) => a.actionSimple).filter(Boolean),
    ).size;
    const uniqueActionSize = new Set(realActions.map((a) => a.actionSize)).size;
    const uniqueDirect = new Set(
      realActions.map((a) => (a.direct === true ? "direct" : "indirect")),
    ).size;
    const uniqueOutcome = new Set(
      realActions.map((a) => a.primaryOutcome).filter(Boolean),
    ).size;
    const uniquePrimaryPlayer = new Set(
      realActions
        .map((a) => a.primaryPlayer)
        .filter((v): v is number => v !== null),
    ).size;
    const uniqueSecondaryPlayer = new Set(
      realActions
        .map((a) => a.secondaryPlayer)
        .filter((v): v is number => v !== null),
    ).size;
    const uniquePrimaryDefender = new Set(
      realActions
        .map((a) => a.primaryDefender)
        .filter((v): v is number => v !== null),
    ).size;
    const uniqueSecondaryDefender = new Set(
      realActions
        .map((a) => a.secondaryDefender)
        .filter((v): v is number => v !== null),
    ).size;

    // Count actions per category, applying all OTHER filters (cross-filter pattern)
    // Use realActions so no_action entries don't inflate counts
    const forActionType = applyActionFiltersExcept(realActions, filters, "actionType", lineups);
    const forActionSimple = applyActionFiltersExcept(realActions, filters, "actionSimple", lineups);
    const forActionSize = applyActionFiltersExcept(realActions, filters, "actionSize", lineups);
    const forDirect = applyActionFiltersExcept(realActions, filters, "direct", lineups);
    const forOutcome = applyActionFiltersExcept(realActions, filters, "primaryOutcome", lineups);
    const forPrimaryPlayer = applyActionFiltersExcept(realActions, filters, "primaryPlayer", lineups);
    const forSecondaryPlayer = applyActionFiltersExcept(realActions, filters, "secondaryPlayer", lineups);
    const forPrimaryDefender = applyActionFiltersExcept(realActions, filters, "primaryDefender", lineups);
    const forSecondaryDefender = applyActionFiltersExcept(realActions, filters, "secondaryDefender", lineups);

    // Action type options (with aPPP)
    const actionTypeStats = computeOptionStats(
      forActionType,
      (a) => a.actionType,
    );
    const actionTypeOptions: FilterOption<ActionType>[] = [
      ...actionTypeStats.entries(),
    ]
      .sort((a, b) => b[1].count - a[1].count)
      .map(([value, s]) => ({
        value,
        label: formatActionType(value),
        count: s.count,
        aPPP: s.aPPP,
      }));

    // Action simple options (with aPPP)
    const actionSimpleStats = computeOptionStats(
      forActionSimple,
      (a) => a.actionSimple || null,
    );
    const actionSimpleOptions: FilterOption<string>[] = [
      ...actionSimpleStats.entries(),
    ]
      .sort((a, b) => b[1].count - a[1].count)
      .map(([value, s]) => ({
        value,
        label: value,
        count: s.count,
        aPPP: s.aPPP,
      }));

    // Action size options (with aPPP)
    const actionSizeStats = computeOptionStats(
      forActionSize,
      (a) => a.actionSize,
    );
    const actionSizeOptions: FilterOption<ActionSize>[] = [
      ...actionSizeStats.entries(),
    ]
      .sort((a, b) => b[1].count - a[1].count)
      .map(([value, s]) => ({
        value,
        label: formatActionSize(value),
        count: s.count,
        aPPP: s.aPPP,
      }));

    // Direct options (with aPPP)
    const directStats = computeOptionStats(
      forDirect,
      (a): "direct" | "indirect" => (a.direct === true ? "direct" : "indirect"),
    );
    const directOptions: FilterOption<"direct" | "indirect">[] = [
      {
        value: "direct",
        label: "Direct",
        count: directStats.get("direct")?.count ?? 0,
        aPPP: directStats.get("direct")?.aPPP ?? null,
      },
      {
        value: "indirect",
        label: "Indirect",
        count: directStats.get("indirect")?.count ?? 0,
        aPPP: directStats.get("indirect")?.aPPP ?? null,
      },
    ];

    // Primary outcome options (with aPPP)
    const outcomeStats = computeOptionStats(
      forOutcome,
      (a) => a.primaryOutcome,
    );
    const outcomeOptions: FilterOption<"finish" | "pass">[] = [
      {
        value: "finish",
        label: "Finish",
        count: outcomeStats.get("finish")?.count ?? 0,
        aPPP: outcomeStats.get("finish")?.aPPP ?? null,
      },
      {
        value: "pass",
        label: "Pass",
        count: outcomeStats.get("pass")?.count ?? 0,
        aPPP: outcomeStats.get("pass")?.aPPP ?? null,
      },
    ];

    const buildPlayerOptions = (
      crossFiltered: Action[],
      getKey: (a: Action) => number | null,
    ): PlayerOption[] =>
      [...computeOptionStats<number>(crossFiltered, getKey).entries()]
        .sort((a, b) => b[1].count - a[1].count)
        .map(([idx, s]) => ({
          index: idx,
          name: resolveName(idx),
          count: s.count,
          aPPP: s.aPPP,
        }));

    // Primary player options (with aPPP)
    const primaryPlayerOptions = buildPlayerOptions(
      forPrimaryPlayer,
      (a) => a.primaryPlayer,
    );

    // Secondary player options (with aPPP)
    const secondaryPlayerOptions = buildPlayerOptions(
      forSecondaryPlayer,
      (a) => a.secondaryPlayer,
    );

    // Primary defender options (with aPPP)
    const primaryDefenderOptions = buildPlayerOptions(
      forPrimaryDefender,
      (a) => a.primaryDefender,
    );

    // Secondary defender options (with aPPP)
    const secondaryDefenderOptions = buildPlayerOptions(
      forSecondaryDefender,
      (a) => a.secondaryDefender,
    );

    // Show a filter group only if the full dataset has >1 unique value
    return {
      actionType: { options: actionTypeOptions, show: uniqueActionTypes > 1 },
      actionSimple: {
        options: actionSimpleOptions,
        show: uniqueActionSimple > 1,
      },
      actionSize: { options: actionSizeOptions, show: uniqueActionSize > 1 },
      direct: { options: directOptions, show: uniqueDirect > 1 },
      primaryOutcome: { options: outcomeOptions, show: uniqueOutcome > 1 },
      primaryPlayer: {
        options: primaryPlayerOptions,
        show: uniquePrimaryPlayer > 1,
      },
      secondaryPlayer: {
        options: secondaryPlayerOptions,
        show: uniqueSecondaryPlayer > 1,
      },
      primaryDefender: {
        options: primaryDefenderOptions,
        show: uniquePrimaryDefender > 1,
      },
      secondaryDefender: {
        options: secondaryDefenderOptions,
        show: uniqueSecondaryDefender > 1,
      },
    };
  }, [actions, filters, players, lineups]);
}

// ============================================================================
// Type-Specific Filter Discovery
// ============================================================================

interface TypeSpecificFilterGroup {
  config: TypeSpecificFieldConfig;
  options: FilterOption<string>[];
  /** Only show if >1 unique value in data */
  show: boolean;
}

/**
 * Detect the single action type (if any) and compute type-specific filter options.
 * Returns null if multiple action types are present.
 */
function useTypeSpecificFilterOptions(
  actions: Action[],
  filters: ActionFilters,
  lineups: ActionDetailFiltersProps["lineups"],
): { detectedType: ActionType | null; groups: TypeSpecificFilterGroup[] } {
  return useMemo(() => {
    // Exclude no_action from type-specific filter computation
    const realActions = actions.filter((a) => a.actionType !== "no_action");

    // Determine if we have a single action type.
    // Check the filter state first, then the effective filtered actions.
    // This handles cases like filtering actionSimple to "PR" (a subset of picks)
    // where actionType still has all types selected but the effective data is all picks.
    const selectedTypes = filters.actionType;
    let detectedType: ActionType | null = null;

    if (selectedTypes.size === 1) {
      // User has filtered to a single type via actionType filter
      detectedType = [...selectedTypes][0];
    } else {
      // Check the effective filtered actions — applies actionSimple, actionSize,
      // player, and other filters that may narrow to a single action type
      const filteredReal = applyActionFilters(
        realActions,
        {
          ...filters,
          actionPresence: new Set<"action" | "no_action">(["action"]),
        },
        lineups,
      );
      const typesInFiltered = new Set<ActionType>();
      for (const a of filteredReal) {
        typesInFiltered.add(a.actionType);
        if (typesInFiltered.size > 1) break;
      }
      if (typesInFiltered.size === 1) {
        detectedType = [...typesInFiltered][0];
      } else {
        // Fallback: check if all real actions in the raw data share the same type
        const typesInData = new Set<ActionType>();
        for (const a of realActions) {
          typesInData.add(a.actionType);
          if (typesInData.size > 1) break;
        }
        if (typesInData.size === 1) {
          detectedType = [...typesInData][0];
        }
      }
    }

    if (!detectedType) return { detectedType: null, groups: [] };

    const fieldConfigs = TYPE_SPECIFIC_FIELDS[detectedType];
    if (!fieldConfigs || fieldConfigs.length === 0)
      return { detectedType, groups: [] };

    const groups: TypeSpecificFilterGroup[] = fieldConfigs.map((config) => {
      // Compute cross-filter counts + aPPP for this field (real actions only)
      const crossFiltered = applyActionFiltersExcept(
        realActions,
        filters,
        config.field,
        lineups,
      );
      const fieldStats = computeOptionStats(crossFiltered, (a) => {
        const rawValue = (a as any)[config.field];
        if (rawValue === undefined || rawValue === null) return null;
        return String(rawValue);
      });

      // Format labels
      const options: FilterOption<string>[] = [...fieldStats.entries()]
        .sort((a, b) => b[1].count - a[1].count)
        .map(([value, s]) => ({
          value,
          label:
            config.type === "boolean"
              ? value === "true"
                ? "Yes"
                : "No"
              : config.field === "pickType"
              ? pickTypeLabel(value)
              : formatFieldValue(value),
          count: s.count,
          aPPP: s.aPPP,
        }));

      return {
        config,
        options,
        // Type-specific filters auto-hide when ≤1 unique value
        show: options.length > 1,
      };
    });

    return { detectedType, groups };
  }, [actions, filters, lineups]);
}

/**
 * Format a type-specific field value for display.
 * "ballhandler_finish" → "Ballhandler Finish"
 */
function formatFieldValue(value: string): string {
  return value
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

// ============================================================================
// Main Component
// ============================================================================

export default function ActionDetailFilters({
  actions,
  lineups,
  filters,
  onChange,
  disabled = false,
  expandedGroups,
  onExpandGroup,
  onResetGroups,
  onExplorerDimensionClick,
  explorerLoadingKey,
}: ActionDetailFiltersProps) {
  const players = usePlayerTable();

  // When a lineup key is in the breakdown, cross-filter counts should reflect
  // the same broadened pool that Overall + chart already use — otherwise chip
  // counts appear to contradict the summary. Chip SELECTION state still comes
  // from `filters` so user choices are unchanged; only counts use the wider
  // pool.
  const inBreakdownLineupKeys = useMemo(
    () => expandedGroups.filter(isLineupKey) as LineupKey[],
    [expandedGroups],
  );
  const effectiveFilters = useMemo(
    () =>
      inBreakdownLineupKeys.length > 0
        ? stripLineupFilters(filters, inBreakdownLineupKeys)
        : filters,
    [filters, inBreakdownLineupKeys],
  );
  // Helper: create an onExplorerClick callback for a given action detail filter key
  const explorerClickFor = (filterKey: string) => {
    if (!onExplorerDimensionClick) return undefined;
    const dimension = ACTION_KEY_TO_FILTER[filterKey];
    if (!dimension) return undefined;
    return () => onExplorerDimensionClick(dimension);
  };

  // Helper: check if a given filter key's explorer link is currently loading
  const isExplorerLoading = (filterKey: string) => {
    const dimension = ACTION_KEY_TO_FILTER[filterKey];
    return dimension ? explorerLoadingKey === `__dim_${dimension}__` : false;
  };
  // Chance-level filter options — all cross-filtered from ALL actions (including no_action)
  const chanceFilterOptions = useMemo(() => {
    // Action presence (Action / No Action)
    const forPresence = applyActionFiltersExcept(
      actions,
      effectiveFilters,
      "actionPresence",
      lineups,
    );
    const presenceStats = computeOptionStats(
      forPresence,
      (a): "action" | "no_action" =>
        a.actionType === "no_action" ? "no_action" : "action",
    );
    const presenceOpts: FilterOption<"action" | "no_action">[] = [
      {
        value: "action",
        label: "Action",
        count: presenceStats.get("action")?.count ?? 0,
        aPPP: presenceStats.get("action")?.aPPP ?? null,
      },
      {
        value: "no_action",
        label: "No Action",
        count: presenceStats.get("no_action")?.count ?? 0,
        aPPP: presenceStats.get("no_action")?.aPPP ?? null,
      },
    ];
    const showPresence = presenceOpts.filter((o) => o.count > 0).length > 1;

    // Transition
    const forTransition = applyActionFiltersExcept(
      actions,
      effectiveFilters,
      "transition",
      lineups,
    );
    const transitionStats = computeOptionStats(
      forTransition,
      (a) => a.transition,
    );
    const transitionOpts: FilterOption<string>[] = [
      ...transitionStats.entries(),
    ]
      .sort((a, b) => b[1].count - a[1].count)
      .map(([value, s]) => ({
        value,
        label: value.charAt(0).toUpperCase() + value.slice(1),
        count: s.count,
        aPPP: s.aPPP,
      }));

    // Chance Type
    const forChanceType = applyActionFiltersExcept(
      actions,
      effectiveFilters,
      "chanceType",
      lineups,
    );
    const chanceTypeStats = computeOptionStats(
      forChanceType,
      (a) => a.chanceType,
    );
    const chanceTypeOpts: FilterOption<string>[] = [
      ...chanceTypeStats.entries(),
    ]
      .sort((a, b) => b[1].count - a[1].count)
      .map(([value, s]) => ({
        value,
        label: value.charAt(0).toUpperCase() + value.slice(1),
        count: s.count,
        aPPP: s.aPPP,
      }));

    // Zone (Coverage)
    const forZone = applyActionFiltersExcept(
      actions,
      effectiveFilters,
      "zone",
      lineups,
    );
    const zoneStats = computeOptionStats(forZone, (a) => a.zone);
    const zoneOpts: FilterOption<string>[] = [...zoneStats.entries()]
      .sort((a, b) => b[1].count - a[1].count)
      .map(([value, s]) => ({
        value,
        label: value.charAt(0).toUpperCase() + value.slice(1),
        count: s.count,
        aPPP: s.aPPP,
      }));

    return {
      actionPresence: { options: presenceOpts, show: showPresence },
      transition: { options: transitionOpts, show: transitionOpts.length > 1 },
      chanceType: { options: chanceTypeOpts, show: chanceTypeOpts.length > 1 },
      zone: { options: zoneOpts, show: zoneOpts.length > 1 },
    };
  }, [actions, effectiveFilters, lineups]);

  const showChanceSection =
    chanceFilterOptions.actionPresence.show ||
    chanceFilterOptions.transition.show ||
    chanceFilterOptions.chanceType.show ||
    chanceFilterOptions.zone.show;

  // Whether real actions are included — when false, action filters are irrelevant
  const hasActionChance = filters.actionPresence.has("action");

  const commonOptions = useCommonFilterOptions(
    actions,
    effectiveFilters,
    players,
    lineups,
  );
  const { detectedType, groups: typeSpecificGroups } =
    useTypeSpecificFilterOptions(actions, effectiveFilters, lineups);

  const visibleTypeGroups = typeSpecificGroups.filter((g) => g.show);

  // Dropdown change handlers
  const handleDropdownChange =
    (field: keyof ActionFilters) => (event: any) => {
      const value = event.target.value as number[];
      onChange({ ...filters, [field]: new Set<number>(value) });
    };

  // Whether a shot filter key exists in the hierarchy — disables adding new action keys
  const hasShotKeyInHierarchy = expandedGroups.some(isShotFilterKey);

  // Forward label click with modifier key state
  const handleLabelClick = (groupKey: string, event: React.MouseEvent) => {
    onExpandGroup(groupKey, event.metaKey || event.ctrlKey);
  };

  // Compute hierarchy level for a group key (undefined = not in hierarchy)
  // When a shot key blocks nesting, action labels that aren't already in the hierarchy lose their styling
  const getHierarchyLevel = (groupKey: string): number | undefined => {
    const idx = expandedGroups.indexOf(groupKey);
    return idx >= 0 ? idx + 1 : undefined;
  };

  // Whether an action label should show clickable/expandable styling
  // Disabled when a shot key blocks nesting (unless the action key is already in the hierarchy)
  const isLabelClickable = (groupKey: string): boolean => {
    if (!hasShotKeyInHierarchy) return true;
    return expandedGroups.includes(groupKey); // Only allow clicking to remove existing keys
  };

  // Whether a group can be nested ("+") — only when breakdown is active, not at max depth,
  // group isn't already in hierarchy, and label is clickable (not blocked by shot key constraint)
  const canNest = (groupKey: string): boolean =>
    expandedGroups.length > 0 &&
    expandedGroups.length < 3 &&
    !expandedGroups.includes(groupKey) &&
    isLabelClickable(groupKey);

  const handleNest = (groupKey: string) => {
    onExpandGroup(groupKey, true); // true = modifier click = nest
  };

  // Type-specific chip toggle handler
  const handleTypeSpecificToggle = (field: string, value: string) => {
    const current = filters.typeSpecific[field];

    if (!current || current.size === 0) {
      // Currently "all" — discover all values and deselect the toggled one
      const allValues = new Set<string>();
      for (const a of actions) {
        const rawValue = (a as any)[field];
        if (rawValue !== undefined && rawValue !== null) {
          allValues.add(String(rawValue));
        }
      }
      allValues.delete(value);
      onChange({
        ...filters,
        typeSpecific: { ...filters.typeSpecific, [field]: allValues },
      });
    } else if (current.has(value)) {
      const newSet = new Set(current);
      newSet.delete(value);
      // If empty after deselect, reset to "all"
      onChange({
        ...filters,
        typeSpecific: {
          ...filters.typeSpecific,
          [field]: newSet.size === 0 ? new Set<string>() : newSet,
        },
      });
    } else {
      const newSet = new Set(current);
      newSet.add(value);
      // Check if all values are now selected — reset to empty (= all)
      const allValues = new Set<string>();
      for (const a of actions) {
        const rawValue = (a as any)[field];
        if (rawValue !== undefined && rawValue !== null) {
          allValues.add(String(rawValue));
        }
      }
      if (newSet.size === allValues.size) {
        onChange({
          ...filters,
          typeSpecific: { ...filters.typeSpecific, [field]: new Set<string>() },
        });
      } else {
        onChange({
          ...filters,
          typeSpecific: { ...filters.typeSpecific, [field]: newSet },
        });
      }
    }
  };

  return (
    <Stack spacing={1.5}>
      {/* ================================================================ */}
      {/* Chance Filters — Action / No Action toggle                       */}
      {/* ================================================================ */}

      {showChanceSection && (
        <>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: "0.05em",
            }}
          >
            Chance Filters
          </Typography>

          {chanceFilterOptions.actionPresence.show && (
            <FilterChipGroup
              label="Action"
              options={chanceFilterOptions.actionPresence.options}
              selectedSet={filters.actionPresence}
              onToggle={(value) =>
                onChange(toggleActionFilter(filters, "actionPresence", value))
              }
              onSetReplace={(s) => onChange({ ...filters, actionPresence: s })}
              disabled={disabled}
              onLabelClick={
                isLabelClickable("actionPresence")
                  ? (e) => handleLabelClick("actionPresence", e)
                  : undefined
              }
              hierarchyLevel={getHierarchyLevel("actionPresence")}
              onNestClick={
                canNest("actionPresence")
                  ? () => handleNest("actionPresence")
                  : undefined
              }
            />
          )}

          {chanceFilterOptions.transition.show && (
            <ChanceChipGroup
              label="Transition"
              options={chanceFilterOptions.transition.options}
              selected={filters.transition}
              onChange={(newSet) =>
                onChange({ ...filters, transition: newSet })
              }
              onSetReplace={(s) => onChange({ ...filters, transition: s })}
              disabled={disabled}
              allOptions={chanceFilterOptions.transition.options}
              onLabelClick={
                isLabelClickable("transition")
                  ? (e) => handleLabelClick("transition", e)
                  : undefined
              }
              hierarchyLevel={getHierarchyLevel("transition")}
              onNestClick={
                canNest("transition")
                  ? () => handleNest("transition")
                  : undefined
              }
              onExplorerClick={explorerClickFor("transition")}
              explorerLoading={isExplorerLoading("transition")}
            />
          )}

          {chanceFilterOptions.chanceType.show && (
            <ChanceChipGroup
              label="Chance Type"
              options={chanceFilterOptions.chanceType.options}
              selected={filters.chanceType}
              onChange={(newSet) =>
                onChange({ ...filters, chanceType: newSet })
              }
              onSetReplace={(s) => onChange({ ...filters, chanceType: s })}
              disabled={disabled}
              allOptions={chanceFilterOptions.chanceType.options}
              onLabelClick={
                isLabelClickable("chanceType")
                  ? (e) => handleLabelClick("chanceType", e)
                  : undefined
              }
              hierarchyLevel={getHierarchyLevel("chanceType")}
              onNestClick={
                canNest("chanceType")
                  ? () => handleNest("chanceType")
                  : undefined
              }
              onExplorerClick={explorerClickFor("chanceType")}
              explorerLoading={isExplorerLoading("chanceType")}
            />
          )}

          {chanceFilterOptions.zone.show && (
            <ChanceChipGroup
              label="Coverage"
              options={chanceFilterOptions.zone.options}
              selected={filters.zone}
              onChange={(newSet) => onChange({ ...filters, zone: newSet })}
              onSetReplace={(s) => onChange({ ...filters, zone: s })}
              disabled={disabled}
              allOptions={chanceFilterOptions.zone.options}
              onLabelClick={
                isLabelClickable("zone")
                  ? (e) => handleLabelClick("zone", e)
                  : undefined
              }
              hierarchyLevel={getHierarchyLevel("zone")}
              onNestClick={
                canNest("zone") ? () => handleNest("zone") : undefined
              }
              onExplorerClick={explorerClickFor("zone")}
              explorerLoading={isExplorerLoading("zone")}
            />
          )}

          <Divider sx={{ my: 0.5 }} />
        </>
      )}

      {/* ================================================================ */}
      {/* Lineup Filters — chance-scoped, between Chance and Action blocks */}
      {/* ================================================================ */}

      <LineupFilterSection
        actions={actions}
        lineups={lineups}
        filters={filters}
        players={players}
        onChange={onChange}
        disabled={disabled}
        isLabelClickable={isLabelClickable}
        handleLabelClick={handleLabelClick}
        getHierarchyLevel={getHierarchyLevel}
        canNest={canNest}
        handleNest={handleNest}
      />

      {/* ================================================================ */}
      {/* Common Filters — hidden when ≤1 unique value in full dataset     */}
      {/* ================================================================ */}

      {hasActionChance && (
        <>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: "0.05em",
            }}
          >
            General Action Filters
          </Typography>

          {/* Direct */}
          {commonOptions.direct.show && (
            <FilterChipGroup
              label="Direct"
              options={commonOptions.direct.options}
              selectedSet={filters.direct}
              onToggle={(value) =>
                onChange(
                  toggleActionFilter(
                    filters,
                    "direct",
                    value as "direct" | "indirect",
                  ),
                )
              }
              onSetReplace={(s) => onChange({ ...filters, direct: s })}
              disabled={disabled}
              onLabelClick={
                isLabelClickable("direct")
                  ? (e) => handleLabelClick("direct", e)
                  : undefined
              }
              hierarchyLevel={getHierarchyLevel("direct")}
              onNestClick={
                canNest("direct") ? () => handleNest("direct") : undefined
              }
              onExplorerClick={explorerClickFor("direct")}
              explorerLoading={isExplorerLoading("direct")}
            />
          )}

          {/* Action Type */}
          {commonOptions.actionType.show && (
            <FilterChipGroup
              label="Action Type"
              options={commonOptions.actionType.options}
              selectedSet={filters.actionType}
              onToggle={(value) =>
                onChange(
                  toggleActionFilter(
                    filters,
                    "actionType",
                    value as ActionType,
                  ),
                )
              }
              onSetReplace={(s) => onChange({ ...filters, actionType: s })}
              disabled={disabled}
              onLabelClick={
                isLabelClickable("actionType")
                  ? (e) => handleLabelClick("actionType", e)
                  : undefined
              }
              hierarchyLevel={getHierarchyLevel("actionType")}
              onNestClick={
                canNest("actionType")
                  ? () => handleNest("actionType")
                  : undefined
              }
              onExplorerClick={explorerClickFor("actionType")}
              explorerLoading={isExplorerLoading("actionType")}
            />
          )}

          {/* Action Simple */}
          {commonOptions.actionSimple.show && (
            <FilterChipGroup
              label="Action"
              options={commonOptions.actionSimple.options}
              selectedSet={filters.actionSimple}
              isSelectAll={filters.actionSimple.size === 0}
              onSetReplace={(s) => onChange({ ...filters, actionSimple: s })}
              onLabelClick={
                isLabelClickable("actionSimple")
                  ? (e) => handleLabelClick("actionSimple", e)
                  : undefined
              }
              hierarchyLevel={getHierarchyLevel("actionSimple")}
              onToggle={(value) => {
                const newSet = new Set(filters.actionSimple);
                if (filters.actionSimple.size === 0) {
                  // Currently showing all — switch to showing only this value
                  for (const opt of commonOptions.actionSimple.options) {
                    newSet.add(opt.value);
                  }
                  newSet.delete(value);
                  onChange({ ...filters, actionSimple: newSet });
                } else if (newSet.has(value)) {
                  newSet.delete(value);
                  onChange({
                    ...filters,
                    actionSimple:
                      newSet.size === 0 ? new Set<string>() : newSet,
                  });
                } else {
                  newSet.add(value);
                  if (
                    newSet.size === commonOptions.actionSimple.options.length
                  ) {
                    onChange({ ...filters, actionSimple: new Set<string>() });
                  } else {
                    onChange({ ...filters, actionSimple: newSet });
                  }
                }
              }}
              disabled={disabled}
              onNestClick={
                canNest("actionSimple")
                  ? () => handleNest("actionSimple")
                  : undefined
              }
              onExplorerClick={explorerClickFor("actionSimple")}
              explorerLoading={isExplorerLoading("actionSimple")}
            />
          )}

          {/* Action Size */}
          {commonOptions.actionSize.show && (
            <FilterChipGroup
              label="Size Matchup"
              options={commonOptions.actionSize.options}
              selectedSet={filters.actionSize}
              onToggle={(value) =>
                onChange(
                  toggleActionFilter(
                    filters,
                    "actionSize",
                    value as ActionSize,
                  ),
                )
              }
              onSetReplace={(s) => onChange({ ...filters, actionSize: s })}
              disabled={disabled}
              onLabelClick={
                isLabelClickable("actionSize")
                  ? (e) => handleLabelClick("actionSize", e)
                  : undefined
              }
              hierarchyLevel={getHierarchyLevel("actionSize")}
              onNestClick={
                canNest("actionSize")
                  ? () => handleNest("actionSize")
                  : undefined
              }
              onExplorerClick={explorerClickFor("actionSize")}
              explorerLoading={isExplorerLoading("actionSize")}
            />
          )}

          {/* Primary Outcome */}
          {commonOptions.primaryOutcome.show && (
            <FilterChipGroup
              label="Outcome"
              options={commonOptions.primaryOutcome.options}
              selectedSet={filters.primaryOutcome}
              onToggle={(value) =>
                onChange(
                  toggleActionFilter(
                    filters,
                    "primaryOutcome",
                    value as "finish" | "pass",
                  ),
                )
              }
              onSetReplace={(s) => onChange({ ...filters, primaryOutcome: s })}
              disabled={disabled}
              onLabelClick={
                isLabelClickable("primaryOutcome")
                  ? (e) => handleLabelClick("primaryOutcome", e)
                  : undefined
              }
              hierarchyLevel={getHierarchyLevel("primaryOutcome")}
              onNestClick={
                canNest("primaryOutcome")
                  ? () => handleNest("primaryOutcome")
                  : undefined
              }
              onExplorerClick={explorerClickFor("primaryOutcome")}
              explorerLoading={isExplorerLoading("primaryOutcome")}
            />
          )}

          {/* Player/Defender Dropdowns — 2-column layout */}
          {(commonOptions.primaryPlayer.show ||
            commonOptions.secondaryPlayer.show ||
            commonOptions.primaryDefender.show ||
            commonOptions.secondaryDefender.show) && (
            <Stack direction="row" spacing={1.5}>
              {/* Left column: Players */}
              <Stack spacing={1.5} sx={{ flex: 1 }}>
                {commonOptions.primaryPlayer.show && (
                  <PlayerDropdown
                    label="Primary Player"
                    options={commonOptions.primaryPlayer.options}
                    selected={filters.primaryPlayer}
                    onChange={handleDropdownChange("primaryPlayer")}
                    disabled={disabled}
                    onLabelClick={
                      isLabelClickable("primaryPlayer")
                        ? (e) => handleLabelClick("primaryPlayer", e)
                        : undefined
                    }
                    hierarchyLevel={getHierarchyLevel("primaryPlayer")}
                    onNestClick={
                      canNest("primaryPlayer")
                        ? () => handleNest("primaryPlayer")
                        : undefined
                    }
                  />
                )}
                {commonOptions.secondaryPlayer.show && (
                  <PlayerDropdown
                    label="Secondary Player"
                    options={commonOptions.secondaryPlayer.options}
                    selected={filters.secondaryPlayer}
                    onChange={handleDropdownChange("secondaryPlayer")}
                    disabled={disabled}
                    onLabelClick={
                      isLabelClickable("secondaryPlayer")
                        ? (e) => handleLabelClick("secondaryPlayer", e)
                        : undefined
                    }
                    hierarchyLevel={getHierarchyLevel("secondaryPlayer")}
                    onNestClick={
                      canNest("secondaryPlayer")
                        ? () => handleNest("secondaryPlayer")
                        : undefined
                    }
                  />
                )}
              </Stack>
              {/* Right column: Defenders */}
              <Stack spacing={1.5} sx={{ flex: 1 }}>
                {commonOptions.primaryDefender.show && (
                  <PlayerDropdown
                    label="Primary Defender"
                    options={commonOptions.primaryDefender.options}
                    selected={filters.primaryDefender}
                    onChange={handleDropdownChange("primaryDefender")}
                    disabled={disabled}
                    onLabelClick={
                      isLabelClickable("primaryDefender")
                        ? (e) => handleLabelClick("primaryDefender", e)
                        : undefined
                    }
                    hierarchyLevel={getHierarchyLevel("primaryDefender")}
                    onNestClick={
                      canNest("primaryDefender")
                        ? () => handleNest("primaryDefender")
                        : undefined
                    }
                  />
                )}
                {commonOptions.secondaryDefender.show && (
                  <PlayerDropdown
                    label="Secondary Defender"
                    options={commonOptions.secondaryDefender.options}
                    selected={filters.secondaryDefender}
                    onChange={handleDropdownChange("secondaryDefender")}
                    disabled={disabled}
                    onLabelClick={
                      isLabelClickable("secondaryDefender")
                        ? (e) => handleLabelClick("secondaryDefender", e)
                        : undefined
                    }
                    hierarchyLevel={getHierarchyLevel("secondaryDefender")}
                    onNestClick={
                      canNest("secondaryDefender")
                        ? () => handleNest("secondaryDefender")
                        : undefined
                    }
                  />
                )}
              </Stack>
            </Stack>
          )}

          {/* ================================================================ */}
          {/* Type-Specific Filters — Auto-hide when ≤1 value                  */}
          {/* ================================================================ */}

          {detectedType && visibleTypeGroups.length > 0 && (
            <>
              <Divider sx={{ my: 0.5 }} />
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                }}
              >
                {formatActionType(detectedType)} Filters
              </Typography>

              {visibleTypeGroups.map((group) => {
                const currentSelection =
                  filters.typeSpecific[group.config.field];
                const isSelectAll =
                  !currentSelection || currentSelection.size === 0;

                return (
                  <FilterChipGroup
                    key={group.config.field}
                    label={group.config.label}
                    options={group.options}
                    selectedSet={currentSelection ?? new Set<string>()}
                    isSelectAll={isSelectAll}
                    onToggle={(value) =>
                      handleTypeSpecificToggle(group.config.field, value)
                    }
                    onSetReplace={(s) =>
                      onChange({
                        ...filters,
                        typeSpecific: {
                          ...filters.typeSpecific,
                          [group.config.field]: s,
                        },
                      })
                    }
                    disabled={disabled}
                    onLabelClick={
                      isLabelClickable(group.config.field)
                        ? (e) => handleLabelClick(group.config.field, e)
                        : undefined
                    }
                    hierarchyLevel={getHierarchyLevel(group.config.field)}
                    onNestClick={
                      canNest(group.config.field)
                        ? () => handleNest(group.config.field)
                        : undefined
                    }
                    onExplorerClick={explorerClickFor(group.config.field)}
                    explorerLoading={isExplorerLoading(group.config.field)}
                  />
                );
              })}
            </>
          )}
        </>
      )}
    </Stack>
  );
}

// ============================================================================
// PlayerDropdown - Reusable multi-select dropdown for players/defenders
// ============================================================================

interface PlayerDropdownProps {
  label: string;
  options: PlayerOption[];
  selected: Set<number>;
  onChange: (event: any) => void;
  disabled: boolean;
  onLabelClick?: (event: React.MouseEvent) => void;
  hierarchyLevel?: number;
  /** Callback when nest "+" icon is clicked — adds this group as a nested breakdown */
  onNestClick?: () => void;
  onExplorerClick?: () => void;
  explorerLoading?: boolean;
}

function PlayerDropdown({
  label,
  options,
  selected,
  onChange,
  disabled,
  onLabelClick,
  hierarchyLevel,
  onNestClick,
  onExplorerClick,
  explorerLoading,
}: PlayerDropdownProps) {
  return (
    <Box className="filter-group">
      <Box className="filter-group__label-row">
        <Typography
          variant="caption"
          className="filter-group__label"
          onClick={onLabelClick}
          sx={{
            cursor: onLabelClick ? "pointer" : undefined,
            textDecoration: hierarchyLevel ? "underline" : undefined,
            color: hierarchyLevel ? "primary.main" : undefined,
            "&:hover": onLabelClick ? { color: "primary.main" } : undefined,
          }}
        >
          {label}
          {hierarchyLevel && (
            <span className="filter-group__level-badge">{hierarchyLevel}</span>
          )}
        </Typography>
        {onNestClick && (
          <IconButton
            size="small"
            onClick={onNestClick}
            className="filter-group__nest-btn"
            title="Add as nested breakdown"
          >
            <AddIcon sx={{ fontSize: 14 }} />
          </IconButton>
        )}
        {onExplorerClick &&
          (explorerLoading ? (
            <CircularProgress size={13} sx={{ color: "text.secondary", mx: "3px" }} />
          ) : (
            <IconButton
              size="small"
              onClick={onExplorerClick}
              className="filter-group__nest-btn"
              title="Open in Explorer"
            >
              <OpenInNewOutlined sx={{ fontSize: 13 }} />
            </IconButton>
          ))}
      </Box>
      <FormControl fullWidth size="small" disabled={disabled}>
        <Select
          multiple
          value={Array.from(selected)}
          onChange={onChange}
          input={<OutlinedInput />}
          renderValue={(sel) =>
            sel.length === 0 ? "All" : `${sel.length} selected`
          }
          displayEmpty
          className="player-select"
          MenuProps={{
            PaperProps: { className: "player-select-menu" },
          }}
        >
          {options.map((option) => (
            <MenuItem key={option.index} value={option.index}>
              <Checkbox checked={selected.has(option.index)} size="small" />
              <ListItemText
                primary={`${option.name} (${option.count.toLocaleString()} - ${option.aPPP !== null ? option.aPPP.toFixed(2) : "\u2014"})`}
              />
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );
}

// ============================================================================
// FilterChipGroup - Reusable chip row
// ============================================================================

interface FilterChipGroupProps<T> {
  label: string;
  options: FilterOption<T>[];
  selectedSet: Set<T>;
  onToggle: (value: T) => void;
  disabled: boolean;
  /** If true, treats all options as selected (for "empty = all" semantics) */
  isSelectAll?: boolean;
  /** Callback when label is clicked (for summary expansion). Receives MouseEvent for modifier key detection. */
  onLabelClick?: (event: React.MouseEvent) => void;
  /** Hierarchy level if this group is in the breakdown (1, 2, 3), or undefined if not */
  hierarchyLevel?: number;
  /** Callback when nest "+" icon is clicked — adds this group as a nested breakdown */
  onNestClick?: () => void;
  /** Callback to replace the entire set (for Cmd/Ctrl+click isolate/reset) */
  onSetReplace?: (newSet: Set<T>) => void;
  /** Callback when explorer icon is clicked — opens this group as an explorer dimension */
  onExplorerClick?: () => void;
  explorerLoading?: boolean;
}

function FilterChipGroup<T>({
  label,
  options,
  selectedSet,
  onToggle,
  disabled,
  isSelectAll = false,
  onLabelClick,
  hierarchyLevel,
  onNestClick,
  onExplorerClick,
  explorerLoading,
  onSetReplace,
}: FilterChipGroupProps<T>) {
  const total = options.reduce((sum, o) => sum + o.count, 0);

  const allValues = options.map((o) => o.value);
  const isAllSelected =
    isSelectAll || allValues.every((v) => selectedSet.has(v));

  const handleClick = (value: T, e: React.MouseEvent) => {
    if (disabled) return;
    const isModifier = e.metaKey || e.ctrlKey;

    if (isModifier && onSetReplace) {
      const isSelected = isSelectAll || selectedSet.has(value);
      if (isAllSelected) {
        // Isolate to single value
        onSetReplace(new Set([value]));
      } else if (isSelected) {
        // Reset to all
        onSetReplace(new Set(allValues));
      } else {
        // Isolate to single value
        onSetReplace(new Set([value]));
      }
      return;
    }

    onToggle(value);
  };

  return (
    <Box className="filter-group">
      <Box className="filter-group__label-row">
        <Typography
          variant="caption"
          className="filter-group__label"
          onClick={onLabelClick}
          sx={{
            cursor: onLabelClick ? "pointer" : undefined,
            textDecoration: hierarchyLevel ? "underline" : undefined,
            color: hierarchyLevel ? "primary.main" : undefined,
            "&:hover": onLabelClick ? { color: "primary.main" } : undefined,
          }}
        >
          {label}
          {hierarchyLevel && (
            <span className="filter-group__level-badge">{hierarchyLevel}</span>
          )}
        </Typography>
        {onNestClick && (
          <IconButton
            size="small"
            onClick={onNestClick}
            className="filter-group__nest-btn"
            title="Add as nested breakdown"
          >
            <AddIcon sx={{ fontSize: 14 }} />
          </IconButton>
        )}
        {onExplorerClick &&
          (explorerLoading ? (
            <CircularProgress size={13} sx={{ color: "text.secondary", mx: "3px" }} />
          ) : (
            <IconButton
              size="small"
              onClick={onExplorerClick}
              className="filter-group__nest-btn"
              title="Open in Explorer"
            >
              <OpenInNewOutlined sx={{ fontSize: 13 }} />
            </IconButton>
          ))}
      </Box>
      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
        {options.map((option) => {
          const isSelected = isSelectAll || selectedSet.has(option.value);
          const pppColor = getHeatColor(option.aPPP, 1.05, 1.25);

          return (
            <Chip
              key={String(option.value)}
              label={
                <div className="filter-chip__content">
                  <div className="filter-chip__name">{option.label}</div>
                  <div className="filter-chip__stats">
                    <span className="filter-chip__stat">
                      {formatPct(option.count, total)}
                    </span>
                    <span
                      className="filter-chip__stat"
                      style={
                        pppColor
                          ? {
                              backgroundColor: pppColor.bg,
                              color: pppColor.text,
                              textShadow: pppColor.textShadow,
                            }
                          : undefined
                      }
                    >
                      {option.aPPP !== null ? option.aPPP.toFixed(2) : "\u2014"}
                    </span>
                  </div>
                </div>
              }
              size="small"
              variant="outlined"
              onClick={(e) => handleClick(option.value, e)}
              disabled={disabled || option.count === 0}
              className={`filter-chip ${isSelected ? "filter-chip--selected" : ""}`}
            />
          );
        })}
      </Stack>
    </Box>
  );
}

// ============================================================================
// ChanceChipGroup — "empty = all" toggle chips for chance-level filters
// ============================================================================

interface ChanceChipGroupProps {
  label: string;
  options: FilterOption<string>[];
  selected: Set<string>;
  onChange: (newSet: Set<string>) => void;
  disabled: boolean;
  allOptions: FilterOption<string>[];
  onLabelClick?: (event: React.MouseEvent) => void;
  hierarchyLevel?: number;
  /** Callback when nest "+" icon is clicked — adds this group as a nested breakdown */
  onNestClick?: () => void;
  /** Callback to replace the entire set (for Cmd/Ctrl+click isolate/reset) */
  onSetReplace?: (newSet: Set<string>) => void;
  onExplorerClick?: () => void;
  explorerLoading?: boolean;
}

function ChanceChipGroup({
  label,
  options,
  selected,
  onChange,
  disabled,
  allOptions,
  onLabelClick,
  hierarchyLevel,
  onNestClick,
  onSetReplace,
  onExplorerClick,
  explorerLoading,
}: ChanceChipGroupProps) {
  const total = options.reduce((sum, o) => sum + o.count, 0);
  const isSelectAll = selected.size === 0;

  const handleModifierClick = (value: string, e: React.MouseEvent) => {
    if (disabled) return;
    const isModifier = e.metaKey || e.ctrlKey;

    if (isModifier && onSetReplace) {
      const isSelected = isSelectAll || selected.has(value);
      if (isSelectAll) {
        onSetReplace(new Set([value]));
      } else if (isSelected) {
        onSetReplace(new Set<string>()); // Reset to all (empty = all)
      } else {
        onSetReplace(new Set([value]));
      }
      return;
    }

    handleToggle(value);
  };

  const handleToggle = (value: string) => {
    if (disabled) return;
    if (isSelectAll) {
      // Currently showing all — switch to showing all EXCEPT this value
      const newSet = new Set(allOptions.map((o) => o.value));
      newSet.delete(value);
      onChange(newSet);
    } else if (selected.has(value)) {
      const newSet = new Set(selected);
      newSet.delete(value);
      // If empty after deselect, reset to "all"
      onChange(newSet.size === 0 ? new Set<string>() : newSet);
    } else {
      const newSet = new Set(selected);
      newSet.add(value);
      // If all values selected, reset to empty (= all)
      if (newSet.size === allOptions.length) {
        onChange(new Set<string>());
      } else {
        onChange(newSet);
      }
    }
  };

  return (
    <Box className="filter-group">
      <Box className="filter-group__label-row">
        <Typography
          variant="caption"
          className="filter-group__label"
          onClick={onLabelClick}
          sx={{
            cursor: onLabelClick ? "pointer" : undefined,
            textDecoration: hierarchyLevel ? "underline" : undefined,
            color: hierarchyLevel ? "primary.main" : undefined,
            "&:hover": onLabelClick ? { color: "primary.main" } : undefined,
          }}
        >
          {label}
          {hierarchyLevel && (
            <span className="filter-group__level-badge">{hierarchyLevel}</span>
          )}
        </Typography>
        {onNestClick && (
          <IconButton
            size="small"
            onClick={onNestClick}
            className="filter-group__nest-btn"
            title="Add as nested breakdown"
          >
            <AddIcon sx={{ fontSize: 14 }} />
          </IconButton>
        )}
        {onExplorerClick &&
          (explorerLoading ? (
            <CircularProgress size={13} sx={{ color: "text.secondary", mx: "3px" }} />
          ) : (
            <IconButton
              size="small"
              onClick={onExplorerClick}
              className="filter-group__nest-btn"
              title="Open in Explorer"
            >
              <OpenInNewOutlined sx={{ fontSize: 13 }} />
            </IconButton>
          ))}
      </Box>
      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
        {options.map((option) => {
          const isSelected = isSelectAll || selected.has(option.value);
          const pppColor = getHeatColor(option.aPPP, 1.05, 1.25);

          return (
            <Chip
              key={option.value}
              label={
                <div className="filter-chip__content">
                  <div className="filter-chip__name">{option.label}</div>
                  <div className="filter-chip__stats">
                    <span className="filter-chip__stat">
                      {formatPct(option.count, total)}
                    </span>
                    <span
                      className="filter-chip__stat"
                      style={
                        pppColor
                          ? {
                              backgroundColor: pppColor.bg,
                              color: pppColor.text,
                              textShadow: pppColor.textShadow,
                            }
                          : undefined
                      }
                    >
                      {option.aPPP !== null ? option.aPPP.toFixed(2) : "\u2014"}
                    </span>
                  </div>
                </div>
              }
              size="small"
              variant="outlined"
              onClick={(e) => handleModifierClick(option.value, e)}
              disabled={disabled || option.count === 0}
              className={`filter-chip ${isSelected ? "filter-chip--selected" : ""}`}
            />
          );
        })}
      </Stack>
    </Box>
  );
}

// ============================================================================
// LineupFilterSection — four on/off-court player filters with any/all mode
// ============================================================================

const LINEUP_CHIP_CONFIG: {
  key: LineupKey;
  label: string;
  side: "off" | "def";
  onCourt: boolean;
}[] = [
  { key: "offenseOnCourt", label: "Offense On Court", side: "off", onCourt: true },
  { key: "offenseOffCourt", label: "Offense Off Court", side: "off", onCourt: false },
  { key: "defenseOnCourt", label: "Defense On Court", side: "def", onCourt: true },
  { key: "defenseOffCourt", label: "Defense Off Court", side: "def", onCourt: false },
];

interface LineupFilterSectionProps {
  actions: Action[];
  lineups: ActionDetailFiltersProps["lineups"];
  filters: ActionFilters;
  players: string[];
  onChange: (filters: ActionFilters) => void;
  disabled: boolean;
  isLabelClickable: (key: string) => boolean;
  handleLabelClick: (key: string, event: React.MouseEvent) => void;
  getHierarchyLevel: (key: string) => number | undefined;
  canNest: (key: string) => boolean;
  handleNest: (key: string) => void;
}

function LineupFilterSection({
  actions,
  lineups,
  filters,
  players,
  onChange,
  disabled,
  isLabelClickable,
  handleLabelClick,
  getHierarchyLevel,
  canNest,
  handleNest,
}: LineupFilterSectionProps) {
  // Universe of player indices available on each side across the action sample.
  const { offIdxs, defIdxs } = useMemo(() => {
    const off = new Set<number>();
    const def = new Set<number>();
    for (const a of actions) {
      if (!a.chanceId) continue;
      const lu = lineups[a.chanceId];
      if (!lu) continue;
      for (const p of lu.off) off.add(p);
      for (const p of lu.def) def.add(p);
    }
    return { offIdxs: off, defIdxs: def };
  }, [actions, lineups]);

  // Hide the whole section if no lineup data is available.
  if (offIdxs.size === 0 && defIdxs.size === 0) return null;

  return (
    <>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{
          fontWeight: 600,
          textTransform: "uppercase",
          letterSpacing: "0.05em",
        }}
      >
        Lineups
      </Typography>

      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
        {/* Left column: offense */}
        <Stack spacing={1.5} sx={{ flex: 1, minWidth: 0 }}>
          {LINEUP_CHIP_CONFIG.slice(0, 2).map((cfg) => (
            <LineupFilterChip
              key={cfg.key}
              config={cfg}
              filters={filters}
              players={players}
              universe={cfg.side === "off" ? offIdxs : defIdxs}
              onChange={onChange}
              disabled={disabled}
              isLabelClickable={isLabelClickable}
              handleLabelClick={handleLabelClick}
              getHierarchyLevel={getHierarchyLevel}
              canNest={canNest}
              handleNest={handleNest}
            />
          ))}
        </Stack>
        {/* Right column: defense */}
        <Stack spacing={1.5} sx={{ flex: 1, minWidth: 0 }}>
          {LINEUP_CHIP_CONFIG.slice(2, 4).map((cfg) => (
            <LineupFilterChip
              key={cfg.key}
              config={cfg}
              filters={filters}
              players={players}
              universe={cfg.side === "off" ? offIdxs : defIdxs}
              onChange={onChange}
              disabled={disabled}
              isLabelClickable={isLabelClickable}
              handleLabelClick={handleLabelClick}
              getHierarchyLevel={getHierarchyLevel}
              canNest={canNest}
              handleNest={handleNest}
            />
          ))}
        </Stack>
      </Stack>

      <Divider sx={{ my: 0.5 }} />
    </>
  );
}

interface LineupFilterChipProps {
  config: (typeof LINEUP_CHIP_CONFIG)[number];
  filters: ActionFilters;
  players: string[];
  universe: Set<number>;
  onChange: (filters: ActionFilters) => void;
  disabled: boolean;
  isLabelClickable: (key: string) => boolean;
  handleLabelClick: (key: string, event: React.MouseEvent) => void;
  getHierarchyLevel: (key: string) => number | undefined;
  canNest: (key: string) => boolean;
  handleNest: (key: string) => void;
}

function LineupFilterChip({
  config,
  filters,
  players,
  universe,
  onChange,
  disabled,
  isLabelClickable,
  handleLabelClick,
  getHierarchyLevel,
  canNest,
  handleNest,
}: LineupFilterChipProps) {
  const { key, label } = config;
  const selected = filters[key];
  const mode = filters.lineupMode[key];
  const level = getHierarchyLevel(key);
  const clickable = isLabelClickable(key);

  const options = useMemo(
    () =>
      Array.from(universe)
        .map((idx) => ({ index: idx, name: players[idx] ?? `#${idx}` }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [universe, players],
  );

  const handleSelectChange = (event: any) => {
    const value = event.target.value as number[];
    onChange({ ...filters, [key]: new Set<number>(value) });
  };

  const handleModeChange = (newMode: LineupMode) => {
    onChange({
      ...filters,
      lineupMode: { ...filters.lineupMode, [key]: newMode },
    });
  };

  return (
    <Box className="filter-group">
      <Box className="filter-group__label-row">
        <Typography
          variant="caption"
          className="filter-group__label"
          onClick={clickable ? (e) => handleLabelClick(key, e) : undefined}
          sx={{
            cursor: clickable ? "pointer" : undefined,
            textDecoration: level ? "underline" : undefined,
            color: level ? "primary.main" : undefined,
            "&:hover": clickable ? { color: "primary.main" } : undefined,
          }}
        >
          {label}
          {level && <span className="filter-group__level-badge">{level}</span>}
        </Typography>

        {/* Any / All toggle — segmented control; only meaningful when ≥2 players selected */}
        <Box
          role="group"
          aria-label={`${label} match mode`}
          sx={{
            display: "inline-flex",
            ml: 0.5,
            borderRadius: "3px",
            overflow: "hidden",
            border: "1px solid #bdbdbd",
            opacity: disabled ? 0.5 : 1,
          }}
        >
          {(["all", "any"] as LineupMode[]).map((m, i) => {
            const active = mode === m;
            return (
              <Box
                key={m}
                component="button"
                type="button"
                onClick={() => !disabled && handleModeChange(m)}
                disabled={disabled}
                sx={{
                  all: "unset",
                  px: 0.75,
                  height: 18,
                  display: "inline-flex",
                  alignItems: "center",
                  fontSize: "0.65rem",
                  fontWeight: active ? 600 : 400,
                  textTransform: "lowercase",
                  cursor: disabled ? "default" : "pointer",
                  backgroundColor: active ? "#4c4c4c" : "transparent",
                  color: active ? "#fff" : "rgba(0,0,0,0.6)",
                  borderLeft: i === 0 ? "none" : "1px solid #bdbdbd",
                  transition: "background-color 0.1s, color 0.1s",
                  "&:hover": !disabled && !active
                    ? { backgroundColor: "rgba(0,0,0,0.04)" }
                    : undefined,
                }}
              >
                {m}
              </Box>
            );
          })}
        </Box>

        {canNest(key) && (
          <IconButton
            size="small"
            onClick={() => handleNest(key)}
            className="filter-group__nest-btn"
            title="Add as nested breakdown"
          >
            <AddIcon sx={{ fontSize: 14 }} />
          </IconButton>
        )}
      </Box>
      <FormControl fullWidth size="small" disabled={disabled}>
        <Select
          multiple
          value={Array.from(selected)}
          onChange={handleSelectChange}
          input={<OutlinedInput />}
          renderValue={(sel) =>
            sel.length === 0 ? "All" : `${sel.length} selected`
          }
          displayEmpty
          className="player-select"
          MenuProps={{ PaperProps: { className: "player-select-menu" } }}
        >
          {options.map((option) => (
            <MenuItem key={option.index} value={option.index}>
              <Checkbox checked={selected.has(option.index)} size="small" />
              <ListItemText primary={option.name} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );
}
